import { uploadToS3 } from "shared/api/awsS3";

/**
 * 첨부파일 업로드 생명주기 모델.
 *
 * 첨부 목록의 항목은 다음 셋 중 하나다.
 *  - 새 업로드 항목(UploadItem): {localId, raw, name, size, status, url, key, fileId, error}
 *    → 저장 버튼을 눌렀을 때 S3로 업로드되며, 성공/실패 상태를 들고 다닌다.
 *  - 서버에서 불러온 기존 첨부: {id, title, link, ...} (status/raw 없음) → 항상 업로드 완료로 취급.
 *  - 브라우저 raw File: 드롭존이 막 전달한 원본. toUploadItem으로 UploadItem으로 정규화한다.
 */

export const UPLOAD_STATUS = {
  PENDING: "pending", // 아직 업로드 시도 전 (저장 시 업로드 예정)
  UPLOADING: "uploading", // 업로드 진행 중
  UPLOADED: "uploaded", // 업로드 완료 (url 보유)
  FAILED: "failed", // 업로드 실패 (재시도 대상)
};

let sequence = 0;
const nextLocalId = () => {
  sequence += 1;
  return `up_${Date.now()}_${sequence}`;
};

// 브라우저 File → 업로드 항목(UploadItem)
export const createUploadItem = (file) => ({
  localId: nextLocalId(),
  raw: file,
  name: file?.name ?? "첨부파일",
  size: Number.isFinite(file?.size) ? file.size : null,
  status: UPLOAD_STATUS.PENDING,
  url: null,
  key: null,
  fileId: null,
  error: null,
});

// 새 업로드 항목(UploadItem)인지
export const isUploadItem = (item) =>
  !!item &&
  typeof item === "object" &&
  "status" in item &&
  "raw" in item;

// 브라우저 raw File인지
export const isRawFile = (item) =>
  typeof File !== "undefined" && item instanceof File;

// 서버에서 불러온 기존 첨부({id, title, link})인지
export const isExistingAttachment = (file) =>
  !!file &&
  typeof file === "object" &&
  !isUploadItem(file) &&
  !isRawFile(file) &&
  (file.id != null ||
    (file.title != null && file.name == null) ||
    file.link != null);

// raw File이면 UploadItem으로 정규화, 그 외(기존 첨부/이미 항목)는 그대로 둔다.
export const toUploadItem = (item) =>
  isRawFile(item) ? createUploadItem(item) : item;

/**
 * 업로드되지 않은(또는 실패한) 항목만 순차 업로드한다.
 * 항목을 직접 수정하지 않고 상태가 갱신된 새 배열을 반환한다(부분 성공 허용).
 * 이미 uploaded 상태(+url)인 항목은 재업로드하지 않고 그대로 둔다 → 재시도 시 중복 방지.
 */
export const uploadItems = async (items) => {
  const list = Array.isArray(items) ? items : [];
  const result = [];
  for (const item of list) {
    if (item.status === UPLOAD_STATUS.UPLOADED && item.url) {
      result.push(item);
      continue;
    }
    try {
      const uploaded = await uploadToS3(item.raw);
      result.push({
        ...item,
        status: UPLOAD_STATUS.UPLOADED,
        url: uploaded.downloadLink,
        key: uploaded.key,
        fileId: uploaded.fileId,
        error: null,
      });
    } catch (error) {
      result.push({
        ...item,
        status: UPLOAD_STATUS.FAILED,
        error: error?.message || "업로드 실패",
      });
    }
  }
  return result;
};
